import React, { useEffect, useState, useCallback } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { Tooltip } from 'primereact/tooltip';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import FeesSummary from './FeesSummary';


// ==================== TYPES ====================
interface LeaveData {
  pending: number;
  approved: number;
  rejected: number;
  other: number;
  total: number;
  future_total: number;
}

interface GenderData {
  male: number;
  female: number;
  other: number;
  unknown: number;
  total: number;
}

interface TeacherData extends GenderData {
  leaves: LeaveData;
}

interface IssueData {
  academic: number;
  behavioral: number;
  health: number;
  other: number;
  total: number;
}

interface StudentData extends GenderData {
  issues: IssueData;
}

interface GeneralData {
  teachers: TeacherData;
  students: StudentData;
  date: string;
}

interface ApiResponse {
  status: string;
  message: string;
  data: GeneralData;
}

// ==================== CUSTOM HOOK ====================
const useGeneralData = () => {
  const [generalData, setGeneralData] = useState<GeneralData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const today = new Date().toISOString().split('T')[0];

      const response = await apiClient.get<ApiResponse>('/general-data', {
        params: { date: today },
      });

      setGeneralData(response.data.data);
    } catch (err: any) {
      console.error('Failed to fetch general data:', err);

      // Handle authentication errors (token expired/invalid)
      if (
        err.message === 'Invalid or expired token' ||
        err.response?.status === 401 ||
        err.response?.status === 403
      ) {
        setError('Session expired. Redirecting to login...');
        // Interceptor already redirects on refresh failure, but fallback just in case
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
        return;
      }

      setError(
        err.response?.data?.message ||
          err.message ||
          'Failed to load dashboard data. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { generalData, loading, error, refetch: fetchData };
};

// ==================== MAIN COMPONENT ====================
const StatsDashboard: React.FC = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { generalData, loading, error, refetch } = useGeneralData();

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  }, [refetch, isRefreshing]);

  // Loading State
    if (loading && !isRefreshing) {
    return (
      <div
        style={{
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
        <Card
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '20px',
            padding: '2rem',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
            <div
              style={{
                width: 70,
                height: 70,
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)',
              }}
            >
              <i
                className="pi pi-sync"
                style={{
                  color: 'white',
                  fontSize: '2rem',
                  animation: 'spin 1s linear infinite',
                }}
              ></i>
            </div>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontWeight: 700, color: '#1e40af', margin: '0 0 0.5rem 0' }}>
                Loading Dashboard
              </h2>
              <p style={{ color: '#64748b', margin: 0 }}>Fetching latest statistics...</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #fafbff 0%, #f1f5f9 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
        }}
      >
        <Card
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '20px',
            padding: '2rem',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
            <div
              style={{
                width: 70,
                height: 70,
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 20px rgba(239, 68, 68, 0.3)',
              }}
            >
              <i className="pi pi-exclamation-circle" style={{ color: 'white', fontSize: '2rem' }}></i>
            </div>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontWeight: 700, color: '#dc2626', margin: '0 0 0.5rem 0' }}>
                Unable to Load Statistics
              </h2>
              <p style={{ color: '#64748b', margin: '0 0 1.5rem 0' }}>{error}</p>
              <Button
                label={isRefreshing ? 'Retrying...' : 'Retry Loading'}
                onClick={handleRefresh}
                disabled={isRefreshing}
                style={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '0.75rem 2rem',
                  fontWeight: 600,
                  boxShadow: '0 6px 16px rgba(239, 68, 68, 0.3)',
                }}
              />
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!generalData) return null;

  // Breakdown Chips with Tooltips
  const renderBreakdownChip = (
    icon: string,
    value: number,
    color: string,
    bgColor: string,
    title: string,
    uniqueId: string
  ) => (
    <>
      <Tooltip
        target={`.chip-${uniqueId}`}
        content={`${title}: ${value}`}
        position="top"
        style={{
          backgroundColor: color,
          color: 'white',
          fontSize: '0.7rem',
          padding: '6px 10px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}
      />
      <div
        className={`chip-${uniqueId} breakdown-chip`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          backgroundColor: bgColor,
          color: color,
          padding: '2px 6px',
          borderRadius: '11px',
          fontSize: '0.7rem',
          fontWeight: 600,
          border: `1px solid ${color}15`,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        }}
      >
        <i className={`pi ${icon}`} style={{ fontSize: '0.75rem' }}></i>
        <span>{value}</span>
      </div>
    </>
  );

  const renderGenderBreakdown = (data: GenderData, prefix: string) => (
    <div style={{ display: 'flex', gap: '4px', marginTop: '12px', flexWrap: 'wrap' }}>
      {renderBreakdownChip('pi-mars', data.male, '#2563eb', '#eff6ff', 'Male', `${prefix}-male`)}
      {renderBreakdownChip('pi-venus', data.female, '#ec4899', '#fdf2f8', 'Female', `${prefix}-female`)}
      {renderBreakdownChip('pi-users', data.other, '#8b5cf6', '#f5f3ff', 'Other', `${prefix}-other`)}
      {renderBreakdownChip(
        'pi-question-circle',
        data.unknown,
        '#6b7280',
        '#f9fafb',
        'Unknown',
        `${prefix}-unknown`
      )}
    </div>
  );

  const renderLeavesBreakdown = (leaves: LeaveData) => (
    <div style={{ display: 'flex', gap: '4px', marginTop: '12px', flexWrap: 'wrap' }}>
      {renderBreakdownChip('pi-clock', leaves.pending, '#f59e0b', '#fffbeb', 'Pending', 'leave-pending')}
      {renderBreakdownChip('pi-check-circle', leaves.approved, '#10b981', '#ecfdf5', 'Approved', 'leave-approved')}
      {renderBreakdownChip('pi-times-circle', leaves.rejected, '#ef4444', '#fef2f2', 'Rejected', 'leave-rejected')}
      {renderBreakdownChip('pi-ellipsis-h', leaves.other, '#6b7280', '#f9fafb', 'Other', 'leave-other')}
    </div>
  );

  const renderIssuesBreakdown = (issues: IssueData) => (
    <div style={{ display: 'flex', gap: '4px', marginTop: '12px', flexWrap: 'wrap' }}>
      {renderBreakdownChip('pi-book', issues.academic, '#2563eb', '#eff6ff', 'Academic', 'issue-academic')}
      {renderBreakdownChip('pi-user', issues.behavioral, '#f59e0b', '#fffbeb', 'Behavioral', 'issue-behavioral')}
      {renderBreakdownChip('pi-heart', issues.health, '#ef4444', '#fef2f2', 'Health', 'issue-health')}
      {renderBreakdownChip('pi-ellipsis-h', issues.other, '#6b7280', '#f9fafb', 'Other', 'issue-other')}
    </div>
  );

  const statsCards = [
    {
      title: 'Total Students',
      subtitle: 'Active Enrollment',
      value: generalData.students.total.toString(),
      iconClass: 'pi-graduation-cap',
      breakdown: renderGenderBreakdown(generalData.students, 'student'),
      patternId: 'dots-pattern',
    },
    {
      title: 'Total Teachers',
      subtitle: 'Faculty Members',
      value: generalData.teachers.total.toString(),
      iconClass: 'pi-user',
      breakdown: renderGenderBreakdown(generalData.teachers, 'teacher'),
      patternId: 'grid-pattern',
    },
    {
      title: 'Student Issues',
      subtitle: 'Requires Attention',
      value: generalData.students.issues.total.toString(),
      iconClass: 'pi-exclamation-triangle',
      breakdown: renderIssuesBreakdown(generalData.students.issues),
      patternId: 'waves-pattern',
    },
    {
      title: 'Leave Requests',
      subtitle: 'All Time Total',
      value: generalData.teachers.leaves.total.toString(),
      iconClass: 'pi-calendar',
      breakdown: renderLeavesBreakdown(generalData.teachers.leaves),
      patternId: 'circles-pattern',
    },
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
      }}
    >
      <style>{`
        @keyframes slideUp {
          from { 
            opacity: 0; 
            transform: translateY(40px) scale(0.95);
          }
          to { 
            opacity: 1; 
            transform: translateY(0) scale(1);
          }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        .stat-card {
          animation: slideUp 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
          opacity: 0;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
          border-radius: 16px;
          cursor: pointer;
          background: linear-gradient(135deg, #fafbff 0%, #f0f4ff 100%);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(59, 130, 246, 0.08);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          min-height: 200px;
        }
        .stat-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 16px 40px rgba(59, 130, 246, 0.2);
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          border: 1px solid rgba(59, 130, 246, 0.3);
        }
        .stat-card:hover .card-content * {
          color: white !important;
        }
        .stat-card:hover .card-value {
          background: white !important;
          -webkit-background-clip: text !important;
          -webkit-text-fill-color: transparent !important;
          background-clip: text !important;
        }
        .stat-card:hover .icon-container {
          background: rgba(255, 255, 255, 0.2) !important;
          box-shadow: 0 8px 24px rgba(255, 255, 255, 0.3) !important;
          transform: scale(1.1);
        }
        .stat-card:hover .breakdown-chip {
          background: rgba(255, 255, 255, 0.2) !important;
          border-color: rgba(255, 255, 255, 0.4) !important;
          color: white !important;
        }
        .stat-card:hover .breakdown-chip i {
          color: white !important;
        }
        .stat-card:hover .breakdown-chip span {
          color: white !important;
        }
        .stat-card:hover .live-chip {
          background: rgba(255, 255, 255, 0.2) !important;
          border-color: rgba(255, 255, 255, 0.3) !important;
        }
        .stat-card:hover .pattern-overlay {
          opacity: 0.08 !important;
          filter: brightness(2);
        }
        .stat-card:hover .breakdown-divider {
          border-color: rgba(255, 255, 255, 0.2) !important;
        }
        .stat-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent 0%, rgba(59, 130, 246, 0.3) 50%, transparent 100%);
          transition: opacity 0.4s ease;
        }
        .stat-card:hover::before {
          opacity: 0;
        }
        .pattern-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          opacity: 0.04;
          pointer-events: none;
          transition: all 0.4s ease;
        }
        .icon-container {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          box-shadow: 0 6px 16px rgba(59, 130, 246, 0.25);
          margin-bottom: 12px;
          transition: all 0.4s ease;
        }
        .p-card .p-card-body { padding: 20px; }
        .p-card .p-card-content { padding: 0; }
        .refreshing { animation: pulse 1.5s ease-in-out infinite; }
        .spinning { animation: spin 1s linear infinite; }
        .breakdown-chip {
          transition: all 0.3s ease;
        }
        .live-chip {
          transition: all 0.3s ease;
        }
        .card-value {
          transition: all 0.4s ease;
        }
        .breakdown-divider {
          transition: all 0.4s ease;
        }
      `}</style>

      {/* SVG Patterns */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <pattern id="dots-pattern" patternUnits="userSpaceOnUse" width="40" height="40">
            <circle cx="8" cy="8" r="1.5" fill="rgba(59, 130, 246, 0.15)" />
            <circle cx="24" cy="24" r="1" fill="rgba(59, 130, 246, 0.1)" />
          </pattern>
          <pattern id="waves-pattern" patternUnits="userSpaceOnUse" width="40" height="40">
            <path d="M0,20 Q10,10 20,20 T40,20" stroke="rgba(236, 72, 153, 0.12)" strokeWidth="1.5" fill="none" />
            <path d="M0,30 Q10,20 20,30 T40,30" stroke="rgba(236, 72, 153, 0.08)" strokeWidth="1" fill="none" />
          </pattern>
          <pattern id="grid-pattern" patternUnits="userSpaceOnUse" width="40" height="40">
            <path
              d="M0,0 L40,0 M0,20 L40,20 M0,40 L40,40 M0,0 L0,40 M20,0 L20,40 M40,0 L40,40"
              stroke="rgba(79, 172, 254, 0.08)"
              strokeWidth="0.5"
            />
          </pattern>
          <pattern id="circles-pattern" patternUnits="userSpaceOnUse" width="40" height="40">
            <circle cx="10" cy="10" r="3" fill="none" stroke="rgba(250, 112, 154, 0.1)" strokeWidth="1" />
            <circle cx="30" cy="30" r="2" fill="rgba(254, 225, 64, 0.12)" />
          </pattern>
        </defs>
      </svg>

      <div>
        {/* Header */}
        <Card
          style={{
            background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
            color: 'white',
            borderRadius: '20px',
            marginBottom: '24px',
            boxShadow: '0 12px 32px rgba(59, 130, 246, 0.25)',
            position: 'relative',
            overflow: 'hidden',
            border: 'none',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: -30,
              right: -30,
              width: 80,
              height: 80,
              borderRadius: '20px',
              background: 'rgba(255, 255, 255, 0.08)',
            }}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <div
                style={{
                  width: 52,
                  height: 52,
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i className="pi pi-th-large" style={{ fontSize: '24px', color: 'white' }}></i>
              </div>
              <div>
                <h1
                  style={{
                    margin: 0,
                    fontSize: '22px',
                    fontWeight: '700',
                    letterSpacing: '0.01em',
                    lineHeight: 1.2,
                    marginBottom: '8px',
                  }}
                >
                  Analytics Dashboard
                </h1>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    opacity: 0.95,
                    fontSize: '13px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="pi pi-chart-line" style={{ fontSize: 16 }}></i>
                    <span>Live Statistics</span>
                  </div>
                  <span>•</span>
                  <span>{generalData.date}</span>
                  {isRefreshing && (
                    <>
                      <span>•</span>
                      <span className="refreshing">Refreshing...</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <Button
              icon={`pi pi-refresh ${isRefreshing ? 'spinning' : ''}`}
              rounded
              outlined
              onClick={handleRefresh}
              disabled={isRefreshing}
              style={{
                width: 44,
                height: 44,
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: 'white',
              }}
            />
          </div>
        </Card>

        {/* Stats Cards */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px',
            marginBottom: '24px',
          }}
        >
          {statsCards.map((card, index) => (
            <Card
              key={index}
              className="stat-card"
              style={{
                animationDelay: `${index * 0.15}s`,
                opacity: isRefreshing ? 0.7 : undefined,
              }}
            >
              <div className="pattern-overlay" style={{ backgroundImage: `url(#${card.patternId})` }} />

              <div className="card-content" style={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div className="icon-container">
                    <i className={`pi ${card.iconClass}`} style={{ fontSize: '1rem' }}></i>
                  </div>
                  <Chip
                    label="Live"
                    className="live-chip"
                    style={{
                      backgroundColor: 'rgba(34, 197, 94, 0.08)',
                      color: '#16a34a',
                      fontWeight: 600,
                      fontSize: '0.65rem',
                      height: 18,
                      border: '1px solid rgba(34, 197, 94, 0.15)',
                      padding: '0 6px',
                    }}
                  />
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <h3
                      className="card-value"
                      style={{
                        fontWeight: 800,
                        fontSize: '1.5rem',
                        lineHeight: 1,
                        margin: 0,
                        marginBottom: '8px',
                        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      {card.value}
                    </h3>
                    <h6
                      style={{
                        fontWeight: 700,
                        color: '#1e40af',
                        fontSize: '1rem',
                        margin: 0,
                        marginBottom: '4px',
                        letterSpacing: '0.01em',
                      }}
                    >
                      {card.title}
                    </h6>
                    <p
                      style={{
                        color: '#64748b',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        margin: 0,
                        marginBottom: '8px',
                      }}
                    >
                      {card.subtitle}
                    </p>
                  </div>
                  <div className="breakdown-divider" style={{ borderTop: '1px solid rgba(59, 130, 246, 0.08)', paddingTop: '8px', marginTop: '8px' }} />
                  <div>
                    <span
                      style={{
                        color: '#64748b',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        display: 'block',
                        marginBottom: '8px',
                      }}
                    >
                      Breakdown
                    </span>
                    <div className="breakdown-chips">
                      {card.breakdown}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
      <FeesSummary />
    </div>
  );
};

export default StatsDashboard;